import { useTheme } from '@mui/material/styles';
import { formatAsKpi } from 'domain/kpis/utils';
import React from 'react';
import { Area, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { NameType } from 'recharts/types/component/DefaultTooltipContent';
import Card from 'ui/views/cards/Card';
import { asShortDate, asMonthYear, asUnixMillis } from 'util/dateUtils';
import { fontSizeSmall } from 'ui/theme/themeBluePlanet';

export interface LineSeriesPoint {
  x: number;
  y: number;
}

export interface ChartData {
  data: LineSeriesPoint[];
}

interface Props {
  name: string;
  main: ChartData;
  unit?: string;
  showXAxis?: boolean;
  showYAxis?: boolean;
}

export default function AreaChart(props: Props) {
  const main = props.main.data.length > 0 ? props.main : { data: [{ x: asUnixMillis(new Date()), y: 0 }] };

  const theme = useTheme();
  const textColor = theme.bluePlanetPalette.grey.dark;
  const fontWeightMedium = theme.typography.fontWeightMedium;

  return (
    <ResponsiveContainer height={200}>
      <ComposedChart
        data={main.data}
        margin={{
          top: 10,
          right: props.showYAxis ? 10 : 0,
          left: props.showYAxis ? -20 : 0,
          bottom: props.showXAxis ? 20 : 0,
        }}
      >
        <CartesianGrid vertical={false} />
        {props.showXAxis && (
          <XAxis
            dataKey="x"
            tickFormatter={value => asShortDate(value)}
            style={{ fill: textColor, fontWeight: fontWeightMedium as number, fontSize: fontSizeSmall }}
            tickLine={false}
          />
        )}
        {props.showYAxis && (
          <YAxis
            axisLine={false}
            tickLine={false}
            style={{ fill: textColor, fontWeight: fontWeightMedium as number, fontSize: fontSizeSmall }}
            allowDecimals={false}
            width={45}
          />
        )}
        <Tooltip content={<ToolTip unit={props.unit} />} />
        <Area
          type="monotone"
          dataKey="y"
          name={props.name}
          stroke={theme.bluePlanetPalette.blue.main}
          fill={theme.bluePlanetPalette.blue.veryLight}
          fillOpacity={0.4}
          strokeWidth={4}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function ToolTip(props: TooltipProps<number, NameType> & { unit?: string }) {
  const { active, payload, unit } = props;
  if (active && payload && payload.length) {
    return (
      <Card elevation={2}>
        {payload.every(entry => entry.value === undefined) ? (
          <p>No data</p>
        ) : (
          payload.map(p => (
            <p key={p.dataKey}>
              <strong>{formatAsKpi(p.value ?? 0)}</strong> {unit}
            </p>
          ))
        )}
        <p className="text-metadata">{asMonthYear(payload[0]?.payload?.x)}</p>
      </Card>
    );
  }

  return null;
}
